/* eslint-disable no-console */
/* eslint-disable newline-before-return */
import {
  IExistingUpload,
  isExistingUpload,
  isUploadWithFile,
  IUpload,
  IUploadWithFile,
} from "@sw-sw/lib-form";
import instance, { xhrService } from "../xhrService";
import db,{offlineImageCache} from '../../db/db';
import uploadApi,{fileToBase64} from "./upload";
import { v4 as uuidv4 } from 'uuid';
import { getDateOnly } from "../../utils/date";

export type Finding = {
  id: number;
  type: string;
  date_completed: Date | null;
  date_initiated: Date | null;
};

/**
 * Findings by inspection
 */
const url = (a: number) => `/api/inspections/${a}/findings`;

function generateId(id:number, findings:any) {
    if (id !== undefined && id !== null && id !== 0) return id;
    if (findings && findings.length > 0) {
        return findings[findings.length - 1].id + Math.floor(Math.random() * 100);
    }
    return Math.floor(Math.random() * 1000);
}

async function uploadImagesOffline<T extends { id: number 
}>(
  finding: T,
  newUploads: IUploadWithFile[] = [],
  online: boolean = false,
): Promise<T & { finding_uploads: any[] }> {
    const findingObj = await db.findings.get(finding.id).catch((error:any) => {
      console.log('Error getting offline finding', error);
    }
    );

    if(!findingObj) {
      console.log('Finding not found for offline upload:', finding.id);
      return { ...finding, finding_uploads: [] };
    }


  const newImgArr:any=[]

   for(const upload of newUploads) {

      const { file, name, annotations = [], rotation = 0,type,timestamp,gps_latitude,gps_longitude } = upload;
      const guid= upload.guid?upload.guid:uuidv4(); 

      const base64File = await fileToBase64(file);

      const timeStamp = timestamp? timestamp: new Date().toISOString();
      const gpsData= gps_latitude && gps_longitude? {lat:gps_latitude, lon:gps_longitude}:null;


      newImgArr.push({
        annotations:annotations,
        createdAt: new Date().toISOString(),
        deletedAt: null,
        finding_id: finding.id,
        is_completed: false,
        timestamp: timeStamp,
        gpsData:gpsData,
        id:guid,
        upload:{
          GUID: guid,
          mime_type: type,
          name: name,
          timestamp:timeStamp,
          rotation,
          gps_latitude:  gps_latitude,
          gps_longitude:  gps_longitude,
          archived_at:null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: null,

        }
      })


      const syncObj = {
        synced:false,
        payload :{
          file:{annotations,rotation,type,name,file,gpsData,timestamp: timeStamp},
          payload_id: finding.id,
        }
      }

      if(online){
        syncObj.synced=true;
      }

   await db.images.add({
       guid: guid,
       file: base64File,
       type: type,
       name: name,
       payload_id: finding.id,
       sync:{
        ...syncObj,
       }
    })

    offlineImageCache[guid] = base64File;

  }


    await db.findings.update(finding.id, {
      finding_uploads: [
        ...findingObj.finding_uploads? findingObj.finding_uploads: [],
        ...newImgArr.map((upload: any) => ({
          ...upload,
          id: upload.id,
          finding_id: finding.id,
        })),
      ],
    });

    return {
      ...finding,
      finding_uploads: [
        ...findingObj.finding_uploads? findingObj.finding_uploads: [],
        ...newImgArr.map((upload: any) => ({
          ...upload,
          id: upload.id,
          finding_id: finding.id,
        })),

        
      ],
    };

  }


// error carrying the message both as error.message (LegacyTemplateQuestions)
// and error.response.data.message (FindingDetail toast) so it is shown to the
// user instead of the generic "unknown error" fallback
function uploadError(message: string): Error {
  const error: any = new Error(message);

  error.response = { data: { message } };
  return error;
}

async function uploadImages<T extends { id: number
}>(
  finding: T,
  images: IUpload[] = [],
  online:boolean = false
): Promise<T & { finding_uploads: any[] }> {
  if (images.length === 0) {
    return { ...finding, finding_uploads: [] };
  }


  const [newUploads, existingUploads] = extractUploads(images);


  if(!navigator.onLine)
  {
    return uploadImagesOffline(finding, newUploads);
  }

  // iOS Safari can hold a stale File/Blob reference (tab backgrounded for the
  // camera, memory pressure, bfcache restore) and silently send an empty body;
  // verify every file is actually readable before sending
  for (const upload of newUploads) {
    let readable = Boolean(upload.file) && upload.file.size > 0;

    if (readable) {
      try {
        await upload.file.slice(0, 1).arrayBuffer();
      } catch {
        readable = false;
      }
    }
    if (!readable) {
      throw uploadError(
        'One of your photos could not be read — please remove it and re-select it, then try again.',
      );
    }
  }

  const uploads= await uploadApi.finding
    .update(finding.id, newUploads, existingUploads)
    .then(fUploads => {
      return {
        ...finding,
        finding_uploads: fUploads,
      };
    });

    const returned = uploads.finding_uploads ?? [];
    // server returns ALL of the finding's uploads (existing + new), created_at ASC;
    // the rows created by this request are the tail
    const createdTail = returned.slice(Math.max(returned.length - newUploads.length, 0));

    if (createdTail.length < newUploads.length || createdTail.some((fu: any) => !fu?.upload?.GUID)) {
      throw uploadError(
        'Your photos did not reach the server — please check your connection and try again.',
      );
    }

    for(let i =0 ; i<newUploads.length; i++) {
      newUploads[i].guid= createdTail[i].upload.GUID
    }


    if(online) {
    await uploadImagesOffline(finding, newUploads, true)
    }

    return uploads;
}

/**
 * Creates a finding record offline for an inspection
 * @param inspectionId - The ID of the inspection
 * @param formData - Form data containing finding details and images
 * @returns The newly created finding object
 */
async function createFindingOffline(
  inspectionId: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false,
  id: number = 0
): Promise<any> {
  const { images, ...data } = formData;
  const user = await db.user.get(1);

  if(!user) {
    return 
  }
  
  // Create commentable objects if comments exist
  const commentableObj = data.comments ? [{
    author: {
      name: user!.name,
      id: user!.user_id,
      first_name: user!.first_name,
      last_name: user!.last_name
    },
    note: { content: data.comments },
    created_at: new Date().toISOString()
  }] : [];
  
  // Get inspection details
  const inspection = await db.inspections.get(inspectionId);

  if(!inspection) {
    return
  }

  const templateId = inspection!.template_id;
  const inspectionFindings = inspection!.findings;
  
  // Get existing findings and certification
  const findings = await db.findings.where('id').anyOf(inspectionFindings.map((f: any) => f.id)).toArray();
  const existingCertification = await db.certifications.get(inspectionId);
  

  if(!existingCertification || !findings) {
    console.log('No existing certification found for inspection:', inspectionId);
    return 
  }

  // Get finding type details
  const types = await db.findingTypeInspectionTemplate.get(Number(templateId));
  const findingType = types.findingTypes.find((ft: any) => ft.name === data.type);
  const findingTypeObj = {
    is_static_type: findingType.is_static_type,
    is_required_for_cert: findingType.details.is_required_for_cert,
    finding_type_id: findingType.id,
    color: findingType.details.color,
    is_enabled: findingType.details.is_enabled
  };
  
  // Format dates
  const date_initiated = getDateOnly(data.date_initiated) || null
  const date_completed = data.date_completed? getDateOnly(data.date_completed) :null
  const {comments,...rest}=data

  
  // Create finding object
  const findingObj = {
    id: generateId(id, findings),
    InspectionFindingModel: {
      carried_over: false,
      inspection_id: inspectionId
    },
    ...rest,
    ...findingTypeObj,
    inspection_id: inspectionId,
    date_initiated,
    number: findings && findings.length? findings[findings.length - 1].number + 1: 1,
    commentables: commentableObj,
    date_completed,
    sync:{
      synced:false,
      payload:[
        {
          method:'create',
          ...data
        }
      ]
    }
  };

  if(online){
    findingObj.sync.synced=true;
    findingObj.sync.payload=[]

  }
  // Add finding to database
  const findingId = await db.findings.add(findingObj).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });
  
  // Update inspection with new finding
  await db.inspections.update(inspectionId, {
    findings: [...inspectionFindings, findingObj]
  }).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });
  
  // Return the newly created finding
  const finding = await db.findings.get(findingId).catch((error: any) => {
    console.log('Error creating offline finding', error);
    return Promise.reject(error);
  });
  
  // // Update certification if all required findings are completed
    const requiredFindings = [...findings,finding].filter((f: any) => f.is_required_for_cert);
    const completedRequiredFinings = requiredFindings.filter((f: any) => f.date_completed);
    
    if (requiredFindings.length === completedRequiredFinings.length && existingCertification.groupOptions['compliance'] && existingCertification.groupOptions['compliance'].errors.length === 0) {
      existingCertification.groupOptions['compliance'].enabled = true;
      existingCertification.groupOptions['compliance'].errors = [];
      
      await db.certifications.update(inspectionId, existingCertification)
        .catch((error: any) => {
          console.log('Error updating offline certification', error);
          return Promise.reject(error);
        });
      
    }
    else if(existingCertification.groupOptions['compliance']) {
       existingCertification.groupOptions['compliance'].enabled = false;
      if(existingCertification.groupOptions['compliance'].errors.indexOf('Findings need to be completed prior to signing') === -1) {
        existingCertification.groupOptions['compliance'].errors.push('Findings need to be completed prior to signing');
      }
     
      await db.certifications.update(inspectionId, existingCertification)
        .catch((error: any) => {
          console.log('Error updating offline certification', error);
          return Promise.reject(error);
        });
      
    }  

   return {...finding,dissolveFindingImages:false};
}

/**
 * Updates a finding record in an offline database and handles related records
 * @param id The ID of the finding to update
 * @param formData Form data containing finding details and images
 * @returns The updated finding object
 */
async function updateFindingOffline(
  id: number,
  formData: { [k: string]: any; images: IUpload[] },
  online: boolean = false
): Promise<any> {
  const { images, ...data } = formData;
    
  try {
    // Get current user
    const user = await db.user.get(1);
    
    // Get existing finding
    const oldFinding = await db.findings.get(id);

    if( !oldFinding || !user) {
      console.log('Finding not found for update:', id);
      return 
    }
    
    // Process comments if provided
    const commentableObj: any[] = data.comments 
      ? [{
          author: {
            name: user!.name,
            id: user!.user_id,
            first_name: user!.first_name,
            last_name: user!.last_name
          },
          note: { content: data.comments },
          created_at: new Date().toISOString()
        }] 
      : [];
    
    // Combine existing and new comments
    const updatedCommentables = [...oldFinding.commentables, ...commentableObj];
    
    // Format dates
    const date_initiated = data.date_initiated ? getDateOnly(data.date_initiated) : null;
    const date_completed = data.date_completed ? getDateOnly(data.date_completed) : null;

    const existingFinding = await db.findings.get(id);

    const {comments,...rest}=data

    const syncObj={
      synced:false,
        payload:[
          ...existingFinding.sync?.payload,
          {
            method:'update',
            ...data,
            type: data.bulk? existingFinding.type : data.type,
            date_initiated: data.bulk? existingFinding.date_initiated : date_initiated,
            location: data.bulk ? existingFinding.location: data.location,
        
          }
        ]
    }

   if(online){
      syncObj.synced=true;
      syncObj.payload=[]
    }

    // Update finding record
    await db.findings.update(id, {
      ...rest,
      date_initiated: data.bulk? existingFinding.date_initiated : date_initiated,
      date_completed,
      commentables: updatedCommentables,
      sync:{
        ...syncObj
        
      }
    });
    
    // Get inspection and related findings
    const inspection = await db.inspections.get(Number(data.inspectionId));

    if(!inspection) {
      console.log('Inspection not found for update:', data.inspectionId);
      return 
    }
    const inspectionFindings = inspection!.findings;
    const findings = await db.findings.where('id').anyOf(inspectionFindings.map((f: any) => f.id)).toArray();
    
    // Check if all required findings are completed
    const requiredFindings = findings.filter((f: any) => f.is_required_for_cert && f.id !== id);
    const completedRequiredFindings = requiredFindings.filter((f: any) => f.date_completed);
    
    // Update certification if all required findings are completed
    if (requiredFindings.length === completedRequiredFindings.length) {
      const existingCertification = await db.certifications.get(Number(data.inspectionId));
      
      if(!existingCertification) {
        console.log('Certification not found for inspection:', data.inspectionId);
        return 
      }

      existingCertification.groupOptions['compliance'].enabled = true;
      existingCertification.groupOptions['compliance'].errors = [];
      existingCertification.minCertificationDate= date_completed && date_completed>existingCertification.minCertificationDate ? date_completed : existingCertification.minCertificationDate;
      
      await db.certifications.update(Number(data.inspectionId), existingCertification);
    }
    else {
      const existingCertification = await db.certifications.get(Number(data.inspectionId));

      existingCertification.groupOptions['compliance'].enabled = false;
      if(existingCertification.groupOptions['compliance'].errors.indexOf('Findings need to be completed prior to signing') === -1) {
      existingCertification.groupOptions['compliance'].errors.push('Findings need to be completed prior to signing');
      }
      
      await db.certifications.update(Number(data.inspectionId), existingCertification);
    }
    
    // Get updated finding
    const finding = await db.findings.get(id);
    
    // Update inspection with the updated finding
    await db.inspections.update(
      Number(data.inspectionId),
      {
        findings: [...inspection!.findings.filter((f: any) => f.id !== id), finding]
      }
    );
    
    return {...finding,dissolveFindingImages:false};
  } catch (error) {
    console.log('Error updating offline finding', error);
    return Promise.reject(error);
  }
}

function extractUploads(
  images: IUpload[],
): [IUploadWithFile[], IExistingUpload[]] {
  const newUploads: IUploadWithFile[] = [];
  const existingUploads: IExistingUpload[] = [];

  images.forEach(image => {
    if (isUploadWithFile(image)) {
      newUploads.push(image);
    } else if (isExistingUpload(image)) {
      existingUploads.push(image);
    }
  });

  return [newUploads, existingUploads];
}

export const findingApi = {
  index: async(id: number, excludeCompleted: boolean = false) =>
  {
   
    if(navigator.onLine) {
   return instance
      .get(url(id), { params: { excludeCompleted } })
      .then(response => response.data)
    }
    else {

      const inspection =  await db.inspections.get(Number(id))
      const findingIds = inspection?inspection.findings.map(f=>f.id):[]

      const data= await db.findings.where('id').anyOf(findingIds).toArray().then((findings:any) => {
        return findings;
      })
      .catch((error:any) => {
        console.log('Error getting offline findings', error);
        return [];
      });
      
      return data;
    }
  },

  show: async(id: number): Promise<Finding> => {
    const offline = navigator.onLine? false: true;
    if(!offline) {
    return instance.get(`/api/findings/${id}`).then(response => response.data)
    }
    else {
      return await db.findings.get(Number(id)).catch(err => {
        console.log('Error getting offline finding', err);

        return Promise.reject(err);
      }
      )
    }

  },

  create: async(
    inspectionId: number,
    formData: { [k: string]: any; images: IUpload[] },
  ) => {
  const { images, ...data } = formData;


    if(navigator.onLine){
    const newFinding= await instance
      .post(url(inspectionId), data)
      .then(response => response.data)
  

    await createFindingOffline(inspectionId, formData, true, newFinding.id)
    const result = newFinding.dissolveFindingImages===false ? await uploadImages(newFinding, images,true): { ...newFinding, finding_uploads: [] }
    

    return result;

  } else {
    return createFindingOffline(inspectionId, formData)
    .then((finding:any) => finding.dissolveFindingImages===false ? uploadImages(finding, images): { ...finding, finding_uploads: [] }
    .catch((error:any) => {
      console.log('Error creating offline finding', error);
      
      return Promise.reject(error);
    })
    )
  }
  },

  destroy: (id: number, note: string) => {
    if(navigator.onLine){
      return instance
      .delete(`/api/findings/${id}`, { data: { note: note } })
      .then(response => response.data)
  }
  else {
    return db.findings.delete(id).then(() => {

      return db.findings.where('id').equals(id).toArray().then((findings:any) => {
        return findings;
      });
    }).catch((error:any) => {
      console.log('Error deleting offline finding', error);
      return Promise.reject(error);
    });
  }
  
},

  update: async(id: number, formData: { [k: string]: any; images: IUpload[] }) => {
    const { images, completedImages, ...data } = formData;

    if(navigator.onLine){
    const updatedFinding= await instance
      .post(`/api/findings/${id}`, data)
      .then(response => response.data)

      updateFindingOffline(id, formData,true)
       const result = updatedFinding.dissolveFindingImages===false? await uploadImages(updatedFinding, [...images, ...completedImages],true) : { ...updatedFinding, finding_uploads: [] }

     return result;
      
    }
    else
    {
      return updateFindingOffline(id, formData)
      .then((finding:any) => finding.dissolveFindingImages===false? uploadImages(finding, [...images, ...completedImages]) : { ...finding, finding_uploads: [] })
      .catch((error:any) => {
        console.log('Error updating offline finding', error);
        
        return Promise.reject(error);
      })

    }
  },

  bulk: {
    complete: async (
      inspectionId: number,
      data: {
        [imageKeyName: string]: IUpload[];
      } & { finding_ids: number[]; date_completed: Date; comments: string },
    ) => {
      const {
        finding_ids: findingIds,
        date_completed: dateCompleted,
        comments,
        completed_by
      } = data;
      const findingUploads = new Map<number, IUpload[]>();

      findingIds.forEach(id => {
        const imagesKey = `images_${id}`;

        if (Object.prototype.hasOwnProperty.call(data, imagesKey)) {
          findingUploads.set(id, data[imagesKey]);
        }
      });

    if(navigator.onLine){
      const { data: findings }: { data: { id: number,dissolveFindingImages:boolean }[] } =
        await xhrService.put(`/api/inspections/${inspectionId}/findings`, {
          finding_ids: findingIds,
          date_completed: dateCompleted,
          comments,
          completed_by
        });

        for(const findingId of findingIds) {
         
          const formData = {
            date_completed: dateCompleted,
            comments: comments,
            images: findingUploads.get(findingId) || [],
            completedImages: [],
            inspectionId:inspectionId,
            bulk:true
          }

         await updateFindingOffline(findingId, formData,true);

        }

        if(findings[0] && findings[0].dissolveFindingImages===false){
         return await Promise.all(
          findings.map(finding =>
            uploadImages(finding, findingUploads.get(finding.id),true),
          ),
        );
      } else 
        {
          return findings;
        }

      }
      else {
       
        for(const findingId of findingIds) {
         
          const formData = {
            date_completed: dateCompleted,
            comments: comments,
            images: findingUploads.get(findingId) || [],
            completedImages: [],
            inspectionId:inspectionId,
            bulk:true
          }

         await updateFindingOffline(findingId, formData);

        }

        const findings = await db.findings.where('id').anyOf(findingIds).toArray();

        if(findings[0] && !findings[0].dissolveFindingImages){

          return await Promise.all(
            findings.map((finding:any) =>
              uploadImages(finding, findingUploads.get(finding.id)),
            ),
          );
        } else {
          return findings;
        }


      }
    },
    addNotes:  async (inspectionId: number, findingIds: number[], comments: any) => {

      if(navigator.onLine) {
      const noteData= await xhrService.post(
        `/api/inspections/${inspectionId}/findings/notes`,
        {
          findingIds,
          comments,
        },
      );
      
       for(const findingId of findingIds) {
         
          const formData = {
            comments: comments,
            inspectionId:inspectionId,
            bulk:true,
            images:[],
            completedImages:[],
          }

         await updateFindingOffline(findingId, formData,true);
        }

      return noteData;
    } 
    else {

       for(const findingId of findingIds) {
         
          const formData = {
            comments: comments,
            inspectionId:inspectionId,
            bulk:true,
            images:[],
            completedImages:[],
          }

         await updateFindingOffline(findingId, formData);
        }

          const inspection = await db.inspections.get(Number(inspectionId));
          const inspectionFindings = inspection!.findings;
          const findings = await db.findings.where('id').anyOf(inspectionFindings.map((f: any) => f.id)).toArray();
          
          return { data : findings }

    }
    },
  },
  complianceImpact: (
    findingId: number,
    type: string,
    inspectionId: number,
  ): Promise<{ affected_inspections: Array<{ id: number; created_date: string; type: string }> }> =>
    instance
      .get(`/api/findings/${findingId}/compliance-impact`, { params: { type, inspectionId } })
      .then((response) => response.data),

  addSignature: (actionId: number) => {
    return xhrService.get(
      `/api/findings/${actionId}/cert`,
    );
  },
  removeSignature: (actionId: number) => {
    return xhrService.delete(
      `/api/findings/${actionId}/actionsign`,
    );
  },
  deleteNotes: (findingId: number, actionId: number) => {
    return xhrService.delete(
      `/api/findings/${findingId}/cert/${actionId}`,
    );
  },
};

export default findingApi;
